import React, { useEffect, useState, useMemo } from "react";
import TwoFactorSetup from './TwoFactorSetup';
import styled, { keyframes } from "styled-components";
import { useTranslation, translations, jezykStartowy } from "../translations";
import zxcvbn from "zxcvbn";
import { useNavigate } from "react-router-dom"; // Dodaj import
import { API_BASE } from '../config';
import { Banner } from '../components/ui/Banner';
import { IconButton, IconBadge, panelTopHairline } from '../ui/common';
import { scrollToTop } from '../utils/scrollToTop';
import { ArrowLeftIcon } from '../ui/icons';

// SVG Icon Components
const LockIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M6 10v-4a6 6 0 1 1 12 0v4" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
    <rect x="3" y="10" width="18" height="10" rx="2" ry="2" stroke="currentColor" strokeWidth="2" fill="none"/>
  </svg>
);

const ShieldIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
  </svg>
);

const ActivityIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <polyline points="22,12 18,12 15,21 9,3 6,12 2,12" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
  </svg>
);

const SearchIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <circle cx="11" cy="11" r="8" stroke="currentColor" strokeWidth="2"/>
    <path d="m21 21-4.35-4.35" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
  </svg>
);

const MonitorIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <rect x="2" y="3" width="20" height="14" rx="2" ry="2" stroke="currentColor" strokeWidth="2" fill="none"/>
    <line x1="8" y1="21" x2="16" y2="21" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
    <line x1="12" y1="17" x2="12" y2="21" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
  </svg>
);

const InfoIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="2"/>
    <line x1="12" y1="16" x2="12" y2="12" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
    <line x1="12" y1="8" x2="12.01" y2="8" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
  </svg>
);

const CheckIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <polyline points="20,6 9,17 4,12" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
  </svg>
);

const XIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <line x1="18" y1="6" x2="6" y2="18" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
    <line x1="6" y1="6" x2="18" y2="18" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
  </svg>
);

const Container = styled.div`
  max-width: 100%;
  width: 100%;
  margin: 0 auto;
  padding: clamp(12px, 2vw, 32px);
  background: var(--panel-bg);
  border-radius: 16px;
  box-shadow: 0 8px 32px var(--shadow-color);
  color: var(--text);
  font-family: 'Inter', -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif;
  box-sizing: border-box;
  position: relative;
  overflow: hidden;
  min-height: calc(100vh - 48px);

  ${panelTopHairline}

  @media (min-width: 1400px) {
    max-width: 1600px;
  }

  @media (max-width: 768px) {
    padding: 16px;
    border-radius: 12px;
    min-height: auto;
  }
`;

const Header = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
  margin-bottom: 32px;
`;

const TitleSection = styled.div`
  text-align: center;
  flex: 1;
  /* Bez tego domyślne min-width: auto nie pozwala kolumnie zwęzić się poniżej szerokości
     nagłówka, więc na telefonie podtytuł wychodził poza kontener i był przez niego przycinany. */
  min-width: 0;
`;

const Title = styled.h1`
  margin: 0 0 8px 0;
  font-size: 2.25rem;
  font-weight: 800;
  color: var(--text);
  letter-spacing: -0.025em;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 12px;

  /* Lepsza widoczność w dark mode */
  [data-theme='dark'] & {
    background: none;
    background-clip: initial;
    -webkit-background-clip: initial;
    -webkit-text-fill-color: var(--text);
    color: var(--text);
  }
`;

const TitleIcon = styled.div`
  color: var(--primary);
`;

const Subtitle = styled.p`
  margin: 0;
  font-size: 1.125rem;
  color: var(--text-muted);
  font-weight: 400;
`;

const Grid = styled.div`
  display: grid;
  gap: 24px;
  grid-template-columns: 1fr 520px;
  
  @media(max-width: 1024px){
    grid-template-columns: 1fr;
  }
`;

const slideIn = keyframes`
  from { opacity: 0; transform: translateY(20px); }
  to { opacity: 1; transform: translateY(0); }
`;

const Panel = styled.section`
  background: var(--card-bg);
  backdrop-filter: blur(10px);
  border: 1px solid var(--input-border);
  border-radius: 16px;
  padding: 24px;
  box-sizing: border-box;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  animation: ${slideIn} 0.6s ease-out;
  position: relative;
  
  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 12px 40px var(--shadow-color);
    border-color: var(--input-border);
  }
`;

const PanelHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 20px;
  padding-bottom: 12px;
  border-bottom: 2px solid var(--input-border);
`;

// Wspólna plakietka ikony z ui/common.js (IconBadge) — wołający przekazuje `$tone="steel"` itp.
// zamiast `bg="var(--primary)"`, patrz komentarz przy ICON_TONES w ui/common.js.
const PanelIcon = IconBadge;

const PanelTitle = styled.h3`
  margin: 0;
  font-size: 1.25rem;
  font-weight: 700;
  color: var(--text);
`;

const Form = styled.form`
  display: flex;
  flex-direction: column;
  gap: 20px;
`;

const InputGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
  
  @media(min-width: 768px) {
    flex-direction: row;
  }
`;

const InputWrapper = styled.div`
  flex: 1;
  position: relative;
`;

const Input = styled.input`
  width: 100%;
  padding: 14px 16px;
  border: 2px solid ${p => (p['data-error'] ? 'var(--danger)' : 'var(--input-border)')};
  border-radius: 12px;
  font-size: 1rem;
  font-weight: 500;
  background: ${p => (p['data-error'] ? 'var(--danger-bg)' : 'var(--input-bg)')};
  transition: all 0.2s ease;
  box-sizing: border-box;
  color: var(--text);

  &:focus {
    outline: none;
    border-color: ${p => (p['data-error'] ? 'var(--danger)' : 'var(--primary)')};
    background: var(--card-bg);
    box-shadow: var(--focus-ring);
  }
  
  &::placeholder {
    color: var(--text-muted);
    font-weight: 400;
  }
`;

const ButtonGroup = styled.div`
  display: flex;
  gap: 12px;
  justify-content: flex-end;
  flex-wrap: wrap;
  
  @media(max-width: 768px) {
    justify-content: stretch;
  }
`;

const PrimaryButton = styled.button`
  background: var(--primary);
  color: white;
  border: none;
  padding: 12px 24px;
  border-radius: 12px;
  font-size: 1rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
  box-shadow: var(--shadow-primary);
  position: relative;
  overflow: hidden;
  display: flex;
  align-items: center;
  gap: 8px;
  
  &:hover:not(:disabled) {
    transform: translateY(-1px);
    box-shadow: var(--shadow-primary);
  }
  
  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
    transform: none;
  }
  
  @media(max-width: 768px) {
    flex: 1;
  }
`;

const PasswordStrength = styled.div`
  margin-top: 16px;
`;

const StrengthLabel = styled.div`
  font-size: 0.875rem;
  font-weight: 600;
  color: ${props => props.color || 'var(--text-muted)'};
  margin-bottom: 8px;
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const StrengthBar = styled.div`
  width: 100%;
  height: 8px;
  background: var(--input-border);
  border-radius: 6px;
  overflow: hidden;
  margin-bottom: 8px;
`;

const StrengthFill = styled.div`
  height: 100%;
  width: ${props => props.width}%;
  background: ${props => props.color};
  border-radius: 6px;
  transition: all 0.3s ease;
  position: relative;
  
  &::after {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: linear-gradient(90deg, transparent 0%, rgba(255,255,255,0.3) 50%, transparent 100%);
    animation: shimmer 2s infinite;
  }
  
  @keyframes shimmer {
    0% { transform: translateX(-100%); }
    100% { transform: translateX(100%); }
  }
`;

const StrengthTips = styled.div`
  font-size: 0.8rem;
  color: var(--text-muted);
  line-height: 1.4;
  display: flex;
  align-items: flex-start;
  gap: 6px;
`;

const TableContainer = styled.div`
  background: var(--panel-bg);
  border-radius: 12px;
  overflow: hidden;
  border: 1px solid var(--input-border);
`;

const Table = styled.table`
  width: 100%;
  border-collapse: separate;   /* ważne: linia na całej szerokości wiersza */
  border-spacing: 0;
  table-layout: fixed;         /* stałe kolumny */
  font-size: 0.9rem;
`;

const Th = styled.th`
  text-align: left;
  padding: 14px 16px;
  background: var(--panel-bg);
  font-weight: 700;
  color: var(--text);
  font-size: 0.85rem;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  /* bez borderów na komórkach */
`;

const Td = styled.td`
  padding: 14px 16px;
  color: var(--text);
  vertical-align: middle;
  font-weight: 500;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const SessionRow = styled.tr`
  transition: background 0.2s ease;
  border-bottom: 1px solid var(--input-border);  /* jedna równa linia pod wierszem */
  &:hover { background: rgba(59,130,246,.05); }
`;

const StatusBadge = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 6px 10px;     /* ciut węższa odznaka */
  border-radius: 20px;
  font-size: 0.8rem;
  font-weight: 600;
  white-space: nowrap;   /* nie łamie się w dwóch liniach */
  background: ${props => props.active ? 'var(--success)' : 'var(--text-muted)'};
  color: var(--primary-contrast);
  box-shadow: var(--shadow-sm);
`;

const StatusDot = styled.div`
  width: 6px;
  height: 6px;
  border-radius: 50%;
  background: currentColor;
  animation: ${props => props.active ? 'pulse 2s infinite' : 'none'};
  
  @keyframes pulse {
    0%, 100% { opacity: 1; }
    50% { opacity: 0.5; }
  }
`;

// Lista, a nie tabela: panel stoi w węższej kolumnie układu (1fr 520px), a cztery kolumny
// z table-layout: fixed nie mieściły się tam ani na pulpicie, ani na telefonie. TableContainer
// ma overflow: hidden, więc nie wylewały się poza ekran, tylko PRZYCINAŁY nagłówek i przycisk.
const DeviceList = styled.div`
  display: flex;
  flex-direction: column;
`;

const DeviceItem = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: 14px 16px;
  border-bottom: 1px solid var(--input-border);

  &:last-child { border-bottom: none; }
`;

const DeviceInfo = styled.div`
  flex: 1 1 180px;
  min-width: 0;
`;

const DeviceName = styled.div`
  font-weight: 600;
  color: var(--text);
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const DeviceMeta = styled.div`
  margin-top: 2px;
  font-size: 0.85rem;
  color: var(--text-muted);
  overflow-wrap: anywhere;
`;

const RevokeButton = styled.button`
  padding: 8px 14px;
  border-radius: 8px;
  border: 1px solid var(--danger);
  background: transparent;
  color: var(--danger);
  font-size: 0.85rem;
  font-weight: 600;
  cursor: pointer;
  white-space: nowrap;
  transition: background 0.2s ease, color 0.2s ease;

  &:hover:not(:disabled) { background: var(--danger); color: var(--primary-contrast); }
  &:disabled { opacity: 0.5; cursor: not-allowed; }
`;

const DeviceActions = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 12px;
  margin-top: 16px;
`;

const CurrentTag = styled.span`
  font-size: 0.8rem;
  font-weight: 600;
  color: var(--text-muted);
  white-space: nowrap;
`;


const EmptyState = styled.div`
  text-align: center;
  padding: 48px 24px;
  color: var(--text-muted);
  
  .icon {
    font-size: 48px;
    margin-bottom: 16px;
    opacity: 0.7;
    color: var(--text-muted);
  }
  
  .text {
    font-size: 1.1rem;
    font-weight: 500;
  }
`;

const LoadingSpinner = styled.div`
  width: 24px;
  height: 24px;
  border: 3px solid var(--info-border);
  border-top: 3px solid var(--primary);
  border-radius: 50%;
  animation: spin 1s linear infinite;
  margin: 32px auto;
  
  @keyframes spin {
    to { transform: rotate(360deg); }
  }
`;

const TabBar = styled.div`
  display: flex;
  gap: 4px;
  margin-bottom: 20px;
  border-bottom: 2px solid var(--input-border);
`;

const TabButton = styled.button`
  padding: 10px 18px;
  background: none;
  border: none;
  border-bottom: 2px solid ${props => (props.$active ? 'var(--primary)' : 'transparent')};
  margin-bottom: -2px;
  font-size: 0.95rem;
  font-weight: 600;
  color: ${props => (props.$active ? 'var(--primary)' : 'var(--text-muted)')};
  cursor: pointer;
  transition: color 0.2s ease, border-color 0.2s ease;

  &:hover { color: var(--primary); }
`;

const TipItem = styled.div`
  margin-bottom: 12px;
  display: flex;
  align-items: flex-start;
  gap: 8px;
  color: var(--text-muted);
  line-height: 1.6;
  
  .tip-icon {
    margin-top: 2px;
    color: var(--primary);
    flex-shrink: 0;
  }
`;

// ======= przenieś helpery i styled components tutaj (TOP LEVEL) =======
function isRepeatedPattern(s) {
  if (!s) return false;
  if (/^(.)\1+$/.test(s)) return true;
  const maxCheck = Math.max(1, Math.floor(s.length / 2));
  for (let len = 1; len <= maxCheck; len++) {
    const sub = s.slice(0, len);
    if (sub.repeat(Math.ceil(s.length / len)).slice(0, s.length) === s) return true;
  }
  return false;
}
function containsCommonSequence(s) {
  if (!s) return false;
  return /(password|qwerty|1234+|abcd+|asdf|zxcvbn|dadad|abab)/i.test(s);
}
function meetsPasswordPolicy(s){
  const pw = String(s || '');
  const minLen = 8;
  const hasUpper = /[A-Z]/.test(pw);
  const hasLower = /[a-z]/.test(pw);
  const hasDigit = /[0-9]/.test(pw);
  const lenOk = pw.length >= minLen;
  return { ok: lenOk && hasUpper && hasLower && hasDigit, details: { lenOk, hasUpper, hasLower, hasDigit, minLen } };
}

const Checklist = styled.div`
  margin-top: 12px;
  padding: 12px;
  background: rgba(59, 130, 246, 0.05);
  border-radius: 12px;
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const ChecklistItem = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 0.9rem;
  color: ${props => props['data-ok'] ? 'var(--success)' : 'var(--danger)'};
  
  svg {
    flex-shrink: 0;
    width: 18px;
    height: 18px;
    color: currentColor;
  }
`;
// ===================================================================

// === ZAMIANA: opakowanie logiki i JSX wewnątrz komponentu Security ===
export default function Security(props) {
  const { t } = useTranslation();
  const navigate = useNavigate();

  // podstawowe stany używane w pliku
  const [userId, setUserId] = useState(null);
  const [history, setHistory] = useState([]);
  const [audit, setAudit] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [auditLoading, setAuditLoading] = useState(false);
  const [devices, setDevices] = useState([]);
  const [devicesLoading, setDevicesLoading] = useState(false);
  const [revoking, setRevoking] = useState(null);
  const [deviceError, setDeviceError] = useState('');
  const [deviceMessage, setDeviceMessage] = useState('');
  const [confirmRevokeAll, setConfirmRevokeAll] = useState(false);

  const [oldPass, setOldPass] = useState('');
  const [newPass, setNewPass] = useState('');
  const [newPass2, setNewPass2] = useState('');
  const [passError, setPassError] = useState('');
  const [passSuccess, setPassSuccess] = useState('');
  const [passFieldErrors, setPassFieldErrors] = useState({});
  const [passLoading, setPassLoading] = useState(false);

  const MIN_SCORE = 3; // minimalna akceptowalna ocena (możesz dopasować)

  // Stan 2FA
  const [twoFAEnabled, setTwoFAEnabled] = useState(false);
  const [twoFAEnabledAt, setTwoFAEnabledAt] = useState(null);
  const [backupCodesRemaining, setBackupCodesRemaining] = useState(0);
  const [showTwoFASetup, setShowTwoFASetup] = useState(false);
  const [showDisableForm, setShowDisableForm] = useState(false);
  const [disableCode, setDisableCode] = useState('');
  const [twoFALoading, setTwoFALoading] = useState(false);
  const [twoFAError, setTwoFAError] = useState('');

  const [activityTab, setActivityTab] = useState('history');

  const formatDate = (iso) => {
    if (!iso) return "";
    try {
      const d = new Date(iso);
      const lang = jezykStartowy(typeof localStorage !== 'undefined' ? localStorage.getItem('language') : null);
      const monthsShort = translations[lang]?.['calendar.monthsShort'] || null;

      const timeStr = d.toLocaleTimeString(
        lang === 'pl' ? 'pl-PL' : lang === 'de' ? 'de-DE' : 'en-US',
        { hour: '2-digit', minute: '2-digit' }
      );

      if (monthsShort) {
        const day = d.getDate();
        const month = monthsShort[d.getMonth()];
        const year = d.getFullYear();
        return `${day} ${month} ${year}, ${timeStr}`;
      }

      const locale = lang === 'pl' ? 'pl-PL' : lang === 'de' ? 'de-DE' : 'en-US';
      return d.toLocaleString(locale, {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    } catch {
      return iso;
    }
  };

  function getCookie(name){
    if(typeof document === 'undefined') return '';
    const v = document.cookie.split(';').map(c=>c.trim()).find(c=>c.startsWith(name+'='));
    return v ? decodeURIComponent(v.split('=').slice(1).join('=')) : '';
  }

  function fetchWithCsrf(url, opts = {}){
    const headers = opts.headers || {};
    // najpierw spróbuj ciasteczka, jeśli go nie ma — użyj localStorage (fallback)
    const csrf = getCookie('csrf') || (typeof localStorage !== 'undefined' ? localStorage.getItem('csrfToken') : '') || '';
    if(csrf) headers['X-CSRF-Token'] = csrf;
    return fetch(url, { ...opts, headers, credentials: 'include' });
  }

  // scrollToTop importowany z ../utils/scrollToTop (współdzielony, animowany na #root)
 
  // na starcie spróbuj zainicjalizować userId i zsynchronizować csrf (z localStorage lub /api/whoami)
  useEffect(() => {
    let mounted = true;
    const init = async () => {
      try {
        const storedId = (typeof localStorage !== 'undefined') ? localStorage.getItem('userId') : null;
        if (storedId) {
          if (!mounted) return;
          setUserId(storedId);
          return;
        }
        
        // fallback: zapytaj backend o stan sesji
        const res = await fetch(`${API_BASE}/api/whoami`, { method: 'GET', credentials: 'include' });
        if (!mounted) return;
        if (!res.ok) {
          // brak sesji -> upewnij się, że localStorage nie ma starych danych
          try { localStorage.removeItem('csrfToken'); localStorage.removeItem('userId'); localStorage.removeItem('isLoggedIn'); } catch(_) {}
          return;
        }
        const j = await res.json().catch(() => ({}));
        if (j.userId) {
          setUserId(String(j.userId));
          try { localStorage.setItem('userId', String(j.userId)); localStorage.setItem('isLoggedIn','true'); } catch(_) {}
        }
        if (j.csrfToken) {
          try { localStorage.setItem('csrfToken', j.csrfToken); } catch(_) {}
        } else {
          // jeśli backend nie zwrócił tokenu, spróbuj odczytać ciasteczko (już obsługiwane przez fetchWithCsrf)
          const cookieCsrf = getCookie('csrf');
          if (cookieCsrf) try { localStorage.setItem('csrfToken', cookieCsrf); } catch(_) {}
        }
      } catch (e) {
        // nic nie rób - aplikacja zostanie w stanie niezalogowanym
      }
    };
    init();
    return () => { mounted = false; };
  }, []);

  // compute passEvalMemo, passEval, passScore, passPercent etc.
  const passEvalMemo = useMemo(() => {
    const raw = zxcvbn(newPass || '');
    let adjusted = raw.score ?? 0;
    if (isRepeatedPattern(newPass)) adjusted = Math.max(0, adjusted - 2);
    if (containsCommonSequence(newPass)) adjusted = Math.max(0, adjusted - 1);

    // kara za brak spełnienia polityki
    const policy = meetsPasswordPolicy(newPass);
    if (!policy.ok) {
      const missing = [
        policy.details.lenOk ? 0 : 1,
        policy.details.hasUpper ? 0 : 1,
        policy.details.hasLower ? 0 : 1,
        policy.details.hasDigit ? 0 : 1
      ].reduce((a,b)=>a+b,0);
      adjusted = Math.max(0, adjusted - Math.min(2, missing));
    }

    return { raw, adjusted, policy };
  }, [newPass]);

  const passEval = passEvalMemo.raw;
  const passScore = passEvalMemo.adjusted;
  const passPercent = Math.round((passScore / 4) * 100);
  const scoreLabels = [0, 1, 2, 3, 4].map((i) => t(`passwordStrength.${i}`));
  // Gradient 5-stopniowy (bardzo slabe -> silne) — SWIADOMY wyjatek od tokenow: system tonow ma
  // 4 znaczenia (danger/warning/success/info), a miernik sily hasla potrzebuje 5 WYRAZNIE
  // odroznialnych krokow. Skrajne wartosci sa blisko --danger/--success, srodkowe to funkcjonalny
  // gradient bez odpowiednika w tokenach.
  const scoreColors = ['#ef4444', '#f97316', '#f59e0b', '#10b981', '#059669'];

// DODANE: policyCheck używane w JSX (checklista)
  const policyCheck = useMemo(() => meetsPasswordPolicy(newPass), [newPass]);

  // Lista zalogowanych urządzeń. Endpoint GET istniał od dawna, ale nikt go nie wywoływał, więc
  // wylogowania zdalnego po prostu nie było: `POST /logout` kasuje wyłącznie własne ciasteczko.
  const loadDevices = () => {
    setDevicesLoading(true);
    fetch(`${API_BASE}/api/security/sessions`, { credentials: 'include' })
      .then(r => r.json())
      .then(d => setDevices(Array.isArray(d) ? d : []))
      .catch(() => setDevices([]))
      .finally(() => setDevicesLoading(false));
  };

  const revokeDevice = async (id) => {
    setDeviceError('');
    setDeviceMessage('');
    setRevoking(id);
    try {
      const res = await fetchWithCsrf(`${API_BASE}/api/security/sessions/${id}`, { method: 'DELETE' });
      if (!res.ok) throw new Error('failed');
      setDevices(prev => prev.filter(d => d.id !== id));
      setDeviceMessage(t('security.devices.revokedOne'));
    } catch (e) {
      setDeviceError(t('security.devices.revokeFailed'));
    } finally {
      setRevoking(null);
    }
  };

  const revokeOtherDevices = async () => {
    setDeviceError('');
    setDeviceMessage('');
    setRevoking('others');
    try {
      const res = await fetchWithCsrf(`${API_BASE}/api/security/sessions/others`, { method: 'DELETE' });
      if (!res.ok) throw new Error('failed');
      const data = await res.json().catch(() => ({}));
      setDevices(prev => prev.filter(d => d.current));
      setDeviceMessage(
        t('security.devices.revokedAll').replace('{count}', String(data.revoked ?? 0))
      );
    } catch (e) {
      setDeviceError(t('security.devices.revokeFailed'));
    } finally {
      setRevoking(null);
      setConfirmRevokeAll(false);
    }
  };

  useEffect(()=>{
    if(!userId) return;
    setHistoryLoading(true);
    setAuditLoading(true);

    fetch(`${API_BASE}/api/security/login-history?userId=${userId}`, { credentials: 'include' }).then(r=>r.json()).then(d=>setHistory(Array.isArray(d)?d:[])).catch(()=>setHistory([])).finally(()=>setHistoryLoading(false));
    fetch(`${API_BASE}/api/security/audit-logs?userId=${userId}`, { credentials: 'include' }).then(r=>r.json()).then(d=>setAudit(Array.isArray(d)?d:[])).catch(()=>setAudit([])).finally(()=>setAuditLoading(false));
    // loadDevices czyta sesję z ciasteczka, nie zależy od niczego ze stanu poza userId
    loadDevices();
  },[userId]);

  useEffect(() => {
    if (!userId) return;
    fetchWithCsrf(`${API_BASE}/api/2fa/status`)
      .then(r => r.ok ? r.json() : null)
      .then(data => {
        if (data) {
          setTwoFAEnabled(data.enabled);
          setTwoFAEnabledAt(data.enabledAt);
          setBackupCodesRemaining(data.backupCodesRemaining);
        }
      })
      .catch(() => {});
  }, [userId]); // eslint-disable-line

  const handleDisable2FA = async (e) => {
    e.preventDefault();
    setTwoFAError('');
    setTwoFALoading(true);
    try {
      const isBackup = disableCode.includes('-');
      const body = isBackup
        ? { backupCode: disableCode.trim() }
        : { totpCode: disableCode.replace(/\s/g, '') };
      const res = await fetchWithCsrf(`${API_BASE}/api/2fa/disable`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });
      const data = await res.json();
      if (!res.ok) {
        setTwoFAError(data.error || 'Błąd wyłączania 2FA.');
        return;
      }
      setTwoFAEnabled(false);
      setTwoFAEnabledAt(null);
      setBackupCodesRemaining(0);
      setShowDisableForm(false);
      setDisableCode('');
    } catch {
      setTwoFAError('Błąd połączenia z serwerem.');
    } finally {
      setTwoFALoading(false);
    }
  };

  const handleChangePassword = async (e) => {
    e && e.preventDefault();
    setPassError('');
    setPassSuccess('');
    setPassFieldErrors({});

    const fieldErr = {};
    if (!oldPass.trim()) fieldErr.oldPass = true;
    if (!newPass.trim()) fieldErr.newPass = true;
    if (!newPass2.trim()) fieldErr.newPass2 = true;

    // walidacja: userId musi być ustawione
    if (!userId) {
      setPassError('Brak danych sesji. Zaloguj się ponownie.');
      scrollToTop();
      return;
    }
    if (newPass !== newPass2) {
      fieldErr.newPass = true;
      fieldErr.newPass2 = true;
      setPassFieldErrors(fieldErr);
      setPassError(t('security.passwordsDifferent'));
      scrollToTop();
      return;
    }
    if (newPass.length < 6) {
      fieldErr.newPass = true;
      setPassFieldErrors(fieldErr);
      setPassError(t('security.passwordTooShort'));
      scrollToTop();
      return;
    }

    // wymóg polityki: co najmniej 8 znaków, duża + mała litera + liczba
    const policyCheck = meetsPasswordPolicy(newPass);
    if (!policyCheck.ok) {
      const missing = [];
      if (!policyCheck.details.lenOk) missing.push(`min ${policyCheck.details.minLen} znaków`);
      if (!policyCheck.details.hasUpper) missing.push('wielka litera');
      if (!policyCheck.details.hasLower) missing.push('mała litera');
      if (!policyCheck.details.hasDigit) missing.push('cyfra');
      setPassFieldErrors({ ...fieldErr, newPass: true, newPass2: true });
      setPassError(`Hasło musi zawierać: ${missing.join(', ')}.`);
      scrollToTop();
      return;
    }

    if(passScore < MIN_SCORE) {
      setPassFieldErrors({ ...fieldErr, newPass: true, newPass2: true });
      setPassError(t('security.passwordMinStrength', { strength: scoreLabels[3] }));
      scrollToTop();
      return;
    }

    setPassLoading(true);
    try {
      const res = await fetchWithCsrf(`${API_BASE}/api/security/change-password`, {
        method:'POST',
        headers:{'Content-Type':'application/json'},
        body: JSON.stringify({ userId, oldPassword: oldPass, newPassword: newPass })
      });
      const data = await res.json().catch(()=>({}));
      if(!res.ok) {
        const msg = data?.message || data?.error || t('security.changePasswordError');
        const errors = {};
        const lower = (msg || '').toLowerCase();
        const mentionsOld = /(stare|old|current|obecne|aktualne|bieżące|previous)/.test(lower);
        const mentionsNew = /(nowe|new)/.test(lower);
        const mentionsRepeat = /(powtórz|repeat|confirm|confirmation)/.test(lower);

        if (mentionsOld) errors.oldPass = true;
        if (mentionsNew) errors.newPass = true;
        if (mentionsRepeat) errors.newPass2 = true;

        // Jeśli backend zwraca ogólne "Invalid password" itp., traktuj to jak błąd starego hasła.
        if (Object.keys(errors).length === 0 && /(hasło|password)/.test(lower)) {
          const looksLikeWrongOld = /(nieprawid|incorrect|invalid|wrong)/.test(lower);
          if (looksLikeWrongOld || res.status === 401 || res.status === 403) {
            errors.oldPass = true;
          } else {
            errors.newPass = true;
          }
        }
        setPassFieldErrors(errors);
        setPassError(msg);
        scrollToTop();
      } else {
        setPassError('');
        setPassFieldErrors({});
        setPassSuccess(t('security.passwordChanged'));
        setTimeout(() => setPassSuccess(''), 5000);
        setOldPass(""); setNewPass(""); setNewPass2("");
        scrollToTop();
        fetch(`${API_BASE}/api/security/audit-logs?userId=${userId}`, { credentials: 'include' }).then(r=>r.json()).then(d=>setAudit(Array.isArray(d)?d:[])).catch(()=>{});
      }
    } catch {
      setPassError(t('security.changePasswordError'));
      scrollToTop();
    } finally { setPassLoading(false); }
  };

  // JSX (nie zmienione) - zwracane wewnątrz funkcji
  return (
    <Container>
      <Header>
        <IconButton onClick={() => navigate('/profile')} aria-label={t('common.backToProfile')}>
          <ArrowLeftIcon />
        </IconButton>
        <TitleSection>
          <Title>
            <TitleIcon><ShieldIcon /></TitleIcon>
            {t('security.title')}
          </Title>
          <Subtitle>{t('security.subtitle')}</Subtitle>
        </TitleSection>
      </Header>

      {passError && (
        <div style={{ marginBottom: 20 }}><Banner tone="danger">{passError}</Banner></div>
      )}

      {passSuccess && (
        <div style={{ marginBottom: 20 }}><Banner tone="success">{passSuccess}</Banner></div>
      )}

      <Grid>
        <div>
          <Panel>
            <PanelHeader>
              <PanelIcon $tone="steel">
                <LockIcon />
              </PanelIcon>
              <PanelTitle>{t('security.changePasswordButton')}</PanelTitle>
            </PanelHeader>

            <Form onSubmit={handleChangePassword}>
              <InputGroup>
                <InputWrapper>
                  <Input
                    type="password"
                    placeholder={t('security.currentPassword') || t('security.oldPassword') || 'Stare hasło'}
                    value={oldPass}
                    onChange={e => { setOldPass(e.target.value); setPassFieldErrors(prev => ({ ...prev, oldPass: false })); setPassError(''); }}
                    data-error={!!passFieldErrors.oldPass}
                  />
                </InputWrapper>
                <InputWrapper>
                  <Input
                    type="password"
                    placeholder={t('security.newPassword')}
                    value={newPass}
                    onChange={e => { setNewPass(e.target.value); setPassFieldErrors(prev => ({ ...prev, newPass: false })); setPassError(''); }}
                    data-error={!!passFieldErrors.newPass}
                  />
                </InputWrapper>
                <InputWrapper>
                  <Input
                    type="password"
                    placeholder={t('security.repeatPassword')}
                    value={newPass2}
                    onChange={e => { setNewPass2(e.target.value); setPassFieldErrors(prev => ({ ...prev, newPass2: false })); setPassError(''); }}
                    data-error={!!passFieldErrors.newPass2}
                  />
                </InputWrapper>
              </InputGroup>

              {newPass && (
                <PasswordStrength>
                  <StrengthLabel color={scoreColors[passScore]}>
                    <span>{scoreLabels[passScore]}</span>
                    <span>{passPercent}%</span>
                  </StrengthLabel>
                  <StrengthBar>
                    <StrengthFill width={passPercent} color={scoreColors[passScore]} />
                  </StrengthBar>
                  {passEval.feedback?.suggestions?.length > 0 && (
                    <StrengthTips>
                      <InfoIcon />
                      <span>{passEval.feedback.suggestions.join(' • ')}</span>
                    </StrengthTips>
                  )}

                  <Checklist>
                    <ChecklistItem data-ok={policyCheck.details.lenOk}>
                      {policyCheck.details.lenOk ? <CheckIcon /> : <XIcon />}
                      <div>{t('security.policy.minLength', { min: policyCheck.details.minLen })}</div>
                    </ChecklistItem>
                    <ChecklistItem data-ok={policyCheck.details.hasUpper}>
                      {policyCheck.details.hasUpper ? <CheckIcon /> : <XIcon />}
                      <div>{t('security.policy.upperCase')}</div>
                    </ChecklistItem>
                    <ChecklistItem data-ok={policyCheck.details.hasLower}>
                      {policyCheck.details.hasLower ? <CheckIcon /> : <XIcon />}
                      <div>{t('security.policy.lowerCase')}</div>
                    </ChecklistItem>
                    <ChecklistItem data-ok={policyCheck.details.hasDigit}>
                      {policyCheck.details.hasDigit ? <CheckIcon /> : <XIcon />}
                      <div>{t('security.policy.digit')}</div>
                    </ChecklistItem>
                    <ChecklistItem data-ok={passScore >= 3}>
                      {passScore >= 3 ? <CheckIcon /> : <XIcon />}
                      <div>{t('security.policy.minStrength', { strength: scoreLabels[3] })}</div>
                    </ChecklistItem>
                  </Checklist>
                </PasswordStrength>
              )}

              <ButtonGroup>
                <PrimaryButton
                  type="submit"
                  disabled={passLoading || newPass !== newPass2 || passScore < MIN_SCORE}
                >
                  {passLoading ? (
                    <>
                      <LoadingSpinner style={{ width: '16px', height: '16px', margin: '0' }} />
                      {t('security.changing')}
                    </>
                  ) : (
                    <>
                      <CheckIcon />
                      {t('security.changePasswordButton')}
                    </>
                  )}
                </PrimaryButton>
              </ButtonGroup>
            </Form>
          </Panel>

          <Panel style={{ marginTop: 24 }}>
            <PanelHeader>
              <PanelIcon $tone="steel">
                <ShieldIcon />
              </PanelIcon>
              <PanelTitle>{t('security.twoFA.title')}</PanelTitle>
            </PanelHeader>

            {twoFAEnabled ? (
              <div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 12, flexWrap: 'wrap' }}>
                  <span style={{
                    display: 'inline-flex', alignItems: 'center', gap: 4,
                    background: 'var(--success-bg)', color: 'var(--success)', padding: '3px 10px',
                    borderRadius: 20, fontSize: '0.8rem', fontWeight: 600
                  }}>
                    <CheckIcon /> {t('security.twoFA.active')}
                  </span>
                  {twoFAEnabledAt && (
                    <span style={{ color: 'var(--text-muted)', fontSize: '0.8rem' }}>
                      {t('security.twoFA.enabledOn', { date: formatDate(twoFAEnabledAt) })}
                    </span>
                  )}
                </div>
                <p style={{ color: 'var(--text-muted)', fontSize: '0.875rem', marginBottom: 12 }}>
                  {t('security.twoFA.backupCodesRemaining', { count: backupCodesRemaining })}
                  {backupCodesRemaining <= 2 && (
                    <span style={{ color: 'var(--danger)', marginLeft: 6 }}>
                      {t('security.twoFA.lowCodesWarning')}
                    </span>
                  )}
                </p>

                {!showDisableForm ? (
                  <button
                    onClick={() => { setShowDisableForm(true); setTwoFAError(''); setDisableCode(''); }}
                    style={{
                      background: 'transparent', color: 'var(--danger)',
                      border: '1.5px solid var(--danger-border)', borderRadius: 8,
                      padding: '8px 16px', fontSize: '0.875rem', cursor: 'pointer'
                    }}
                  >
                    {t('security.disable2FA')}
                  </button>
                ) : (
                  <form onSubmit={handleDisable2FA} style={{ maxWidth: 360 }}>
                    <p style={{ color: 'var(--text-muted)', fontSize: '0.875rem', marginBottom: 8 }}>
                      {t('security.twoFA.confirmDisableIntro')}
                    </p>
                    <Input
                      type="text"
                      value={disableCode}
                      onChange={e => { setDisableCode(e.target.value); setTwoFAError(''); }}
                      placeholder={t('security.twoFA.codePlaceholder')}
                      style={{ marginBottom: 10 }}
                    />
                    {twoFAError && (
                      <p style={{ color: 'var(--danger)', fontSize: '0.875rem', marginBottom: 8 }}>{twoFAError}</p>
                    )}
                    <div style={{ display: 'flex', gap: 8 }}>
                      <button
                        type="submit"
                        disabled={twoFALoading || !disableCode.trim()}
                        style={{
                          background: 'var(--danger)', color: 'var(--primary-contrast)', border: 'none',
                          borderRadius: 8, padding: '8px 16px', fontSize: '0.875rem', cursor: 'pointer'
                        }}
                      >
                        {twoFALoading ? t('security.twoFA.disabling') : t('security.twoFA.confirmDisable')}
                      </button>
                      <button
                        type="button"
                        onClick={() => { setShowDisableForm(false); setTwoFAError(''); setDisableCode(''); }}
                        style={{
                          background: 'transparent', color: 'var(--text-muted)',
                          border: '1.5px solid var(--input-border)', borderRadius: 8,
                          padding: '8px 16px', fontSize: '0.875rem', cursor: 'pointer'
                        }}
                      >
                        {t('common.cancel')}
                      </button>
                    </div>
                  </form>
                )}
              </div>
            ) : showTwoFASetup ? (
              <TwoFactorSetup
                onDone={() => {
                  setShowTwoFASetup(false);
                  setTwoFAEnabled(true);
                  setBackupCodesRemaining(10);
                  setTwoFAEnabledAt(new Date().toISOString());
                }}
                onCancel={() => setShowTwoFASetup(false)}
              />
            ) : (
              <div>
                <p style={{ color: 'var(--text-muted)', fontSize: '0.875rem', marginBottom: 12 }}>
                  {t('security.twoFA.intro')}
                </p>
                <button
                  onClick={() => setShowTwoFASetup(true)}
                  style={{
                    background: 'var(--primary)', color: 'var(--primary-contrast)', border: 'none',
                    borderRadius: 8, padding: '9px 20px', fontSize: '0.875rem',
                    fontWeight: 500, cursor: 'pointer'
                  }}
                >
                  {t('security.enable2FA')}
                </button>
              </div>
            )}
          </Panel>

          <Panel style={{ marginTop: 24 }}>
            <PanelHeader>
              <PanelIcon $tone="gold">
                <InfoIcon />
              </PanelIcon>
              <PanelTitle>{t('security.tips.title')}</PanelTitle>
            </PanelHeader>

            <div>
              <TipItem>
                <LockIcon className="tip-icon" />
                <div>
                  <strong>{t('security.tips.passwordTitle')}</strong> - {t('security.tips.passwordDesc')}
                </div>
              </TipItem>
              <TipItem>
                <ShieldIcon className="tip-icon" />
                <div>
                  <strong>{t('security.tips.changeTitle')}</strong> - {t('security.tips.changeDesc')}
                </div>
              </TipItem>
              <TipItem>
                <MonitorIcon className="tip-icon" />
                <div>
                  <strong>{t('security.tips.monitorTitle')}</strong> - {t('security.tips.monitorDesc')}
                </div>
              </TipItem>
            </div>
          </Panel>
        </div>

        <div>
          <Panel>
            <PanelHeader>
              <PanelIcon $tone="dusk">
                <MonitorIcon />
              </PanelIcon>
              <PanelTitle>{t('security.devices.title')}</PanelTitle>
            </PanelHeader>

            <Subtitle style={{ marginTop: -8, marginBottom: 16 }}>
              {t('security.devices.subtitle')}
            </Subtitle>

            {deviceMessage && <div style={{ marginBottom: 20 }}><Banner tone="success">{deviceMessage}</Banner></div>}
            {deviceError && <div style={{ marginBottom: 20 }}><Banner tone="danger">{deviceError}</Banner></div>}

            {/* Konto może mieć wiele aktywnych sesji (różne przeglądarki/urządzenia w oknie
                90 dni, patrz sessionPolicy) — bez limitu wysokości ta lista rozciągała stronę
                i rozjeżdżała kolumny gridu bardziej niż cokolwiek innego na tej stronie. */}
            <TableContainer style={{ maxHeight: 420, overflowY: 'auto' }}>
              {devicesLoading ? (
                <LoadingSpinner />
              ) : devices.length > 0 ? (
                <DeviceList>
                  {devices.map((d) => (
                    <DeviceItem key={d.id}>
                      <DeviceInfo>
                        <DeviceName title={d.agent || ''}>
                          {d.agent || t('security.devices.unknown')}
                        </DeviceName>
                        <DeviceMeta>
                          {t('security.devices.lastActive')} {formatDate(d.last)}
                          {d.ip ? ` · ${d.ip}` : ''}
                        </DeviceMeta>
                      </DeviceInfo>
                      {d.current ? (
                        <CurrentTag>{t('security.devices.thisDevice')}</CurrentTag>
                      ) : (
                        <RevokeButton
                          type="button"
                          onClick={() => revokeDevice(d.id)}
                          disabled={revoking !== null}
                        >
                          {revoking === d.id
                            ? t('security.devices.revoking')
                            : t('security.devices.revoke')}
                        </RevokeButton>
                      )}
                    </DeviceItem>
                  ))}
                </DeviceList>
              ) : (
                <EmptyState>
                  <div className="icon">
                    <MonitorIcon />
                  </div>
                  <div className="text">{t('security.devices.empty')}</div>
                </EmptyState>
              )}
            </TableContainer>

            {devices.some(d => !d.current) && (
              <DeviceActions>
                {confirmRevokeAll ? (
                  <>
                    <CurrentTag>{t('security.devices.confirmAll')}</CurrentTag>
                    <RevokeButton
                      type="button"
                      onClick={revokeOtherDevices}
                      disabled={revoking !== null}
                    >
                      {revoking === 'others'
                        ? t('security.devices.revoking')
                        : t('security.devices.confirmYes')}
                    </RevokeButton>
                    <CurrentTag
                      as="button"
                      type="button"
                      onClick={() => setConfirmRevokeAll(false)}
                      style={{ background: 'none', border: 'none', cursor: 'pointer', textDecoration: 'underline' }}
                    >
                      {t('common.cancel')}
                    </CurrentTag>
                  </>
                ) : (
                  <RevokeButton type="button" onClick={() => setConfirmRevokeAll(true)}>
                    {t('security.devices.revokeAll')}
                  </RevokeButton>
                )}
              </DeviceActions>
            )}
          </Panel>

          {/* Historia logowań i audyt bezpieczeństwa jako zakładki, tuż pod urządzeniami —
              wcześniej stały jako osobna pełna szerokość pod gridem, przez co lewa kolumna
              (krótsza niż ta z urządzeniami) kończyła się pustym pasem zanim zaczynała się
              reszta strony. */}
          <Panel style={{ marginTop: 24 }}>
            <PanelHeader>
              <PanelIcon $tone="clay">
                {activityTab === 'history' ? <ActivityIcon /> : <SearchIcon />}
              </PanelIcon>
              <PanelTitle>
                {activityTab === 'history' ? t('security.loginHistory') : t('security.auditLog')}
              </PanelTitle>
            </PanelHeader>

            <TabBar>
              <TabButton type="button" $active={activityTab === 'history'} onClick={() => setActivityTab('history')}>
                {t('security.loginHistory')}
              </TabButton>
              <TabButton type="button" $active={activityTab === 'audit'} onClick={() => setActivityTab('audit')}>
                {t('security.auditLog')}
              </TabButton>
            </TabBar>

            {activityTab === 'history' ? (
            <TableContainer>
              {historyLoading ? (
                <LoadingSpinner />
              ) : history.length > 0 ? (
                <Table>
                  <thead>
                    <tr>
                      <Th style={{ width: '65%' }}>{t('security.table.time')}</Th>
                      <Th style={{ width: '35%' }}>{t('security.table.status')}</Th>
                    </tr>
                  </thead>
                  <tbody>
                    {history.map((h, i) => (
                      <SessionRow key={i}>
                        <Td>{formatDate(h.ts)}</Td>
                        <Td>
                          <StatusBadge active={h.success}>
                            <StatusDot active={h.success} />
                            {h.success ? t('common.success') : t('common.error')}
                          </StatusBadge>
                        </Td>
                      </SessionRow>
                    ))}
                  </tbody>
                </Table>
              ) : (
                <EmptyState>
                  <div className="icon">
                    <ActivityIcon />
                  </div>
                  <div className="text">{t('security.empty.loginHistory')}</div>
                </EmptyState>
              )}
            </TableContainer>
          ) : (
            <TableContainer>
              {auditLoading ? (
                <LoadingSpinner />
              ) : audit.length > 0 ? (
                <Table>
                  <thead>
                    <tr>
                      <Th style={{ width: '40%' }}>{t('security.table.time')}</Th>
                      <Th style={{ width: '25%' }}>{t('security.action')}</Th>
                      <Th style={{ width: '35%' }}>{t('security.details')}</Th>
                    </tr>
                  </thead>
                  <tbody>
                    {audit.map((a, i) => (
                      <SessionRow key={i}>
                        <Td>{formatDate(a.ts)}</Td>
                        <Td>{(a.action || '').replace(/_/g, ' ')}</Td>
                        <Td style={{ fontSize: '0.85rem', color: 'var(--text-muted)' }}>
                          {a.details ? JSON.stringify(a.details) : ''}
                        </Td>
                      </SessionRow>
                    ))}
                  </tbody>
                </Table>
              ) : (
                <EmptyState>
                  <div className="icon">
                    <SearchIcon />
                  </div>
                  <div className="text">{t('security.empty.auditLog')}</div>
                </EmptyState>
              )}
            </TableContainer>
            )}
          </Panel>
        </div>
      </Grid>
    </Container>
  );
}
// ===================================================================
